// components/features/sectors/SectorForm.tsx
import { useState, useEffect } from 'react';
import { Sector } from '@cabukaka/types';
import { Grid3X3, Image as ImageIcon, Trash2 } from 'lucide-react';

interface SectorFormProps {
  initialData?: Partial<Sector>;
  onSubmit: (data: { name: string; logoFile?: File; existingLogo?: string; order: number }) => Promise<void>;
  isSubmitting?: boolean;
}

interface FormErrors {
  name?: string;
  logo?: string;
  order?: string;
}

export default function SectorForm({
  initialData,
  onSubmit,
  isSubmitting = false
}: SectorFormProps) {
  const [name, setName] = useState(initialData?.name || '');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [order, setOrder] = useState<number>(initialData?.order || 1);
  const [existingLogo, setExistingLogo] = useState<string>(initialData?.logo || '');
  const [logoPreview, setLogoPreview] = useState<string>(initialData?.logo || '');
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (logoFile) {
      const url = URL.createObjectURL(logoFile);
      setLogoPreview(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [logoFile]);

  // Clear error when field changes
  useEffect(() => {
    if (errors.name && name) {
      setErrors(prev => ({ ...prev, name: undefined }));
    }
  }, [name]);

  useEffect(() => {
    if (errors.order && order) {
      setErrors(prev => ({ ...prev, order: undefined }));
    }
  }, [order]);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setExistingLogo('');
      if (errors.logo) {
        setErrors(prev => ({ ...prev, logo: undefined }));
      }
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setExistingLogo('');
    setLogoPreview('');
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!name.trim()) newErrors.name = 'Nom du secteur requis';
    if (!order || order < 1) newErrors.order = 'Numéro d\'ordre valide requis (minimum 1)';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    await onSubmit({
      name: name.trim(),
      order,
      logoFile: logoFile || undefined,
      existingLogo: existingLogo || undefined
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Logo */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
        <div className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 bg-gray-50">
          {logoPreview ? (
            <>
              <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={removeLogo}
                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </>
          ) : (
            <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 transition">
              <ImageIcon className="w-6 h-6 text-gray-400" />
              <span className="text-xs text-gray-500 mt-1">Ajouter</span>
              <input 
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={handleLogoUpload} 
              />
            </label>
          )}
        </div>
        {errors.logo && <p className="text-red-500 text-xs mt-1">{errors.logo}</p>}
      </div>

      {/* Nom */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nom du secteur <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Grid3X3 className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition ${
              errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Ex: Téléphonie, Mode, Électronique"
          />
        </div>
        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
      </div>

      {/* Numéro d'ordre */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Numéro d'ordre <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Grid3X3 className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="number"
            min="1"
            step="1"
            value={order}
            onChange={(e) => setOrder(Number(e.target.value))}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition ${
              errors.order ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Ex: 1"
          />
        </div>
        {errors.order && <p className="text-red-500 text-xs mt-1">{errors.order}</p>}
      </div>

      {/* Boutons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Enregistrement...' : (initialData?.id ? 'Modifier' : 'Créer')}
        </button>
      </div>
    </form>
  );
}